import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  fmtBRL, fmtPct, fmtDateBR, apiErrorMessage, fetchCompanies,
} from '../utils/clinicalUtils';
import './clinical.css';

/**
 * ClinicalDailyEntryPage — formulário de lançamento diário.
 * - Preview LOCAL da conversão enquanto digita, mas grava e reexibe
 *   sempre o valor calculado pelo SERVIDOR (derived na resposta).
 * - Trata o 409 da RN-01 exibindo a mensagem retornada pela API.
 * - Após mutação, atualiza estado local — SEM window.location.reload().
 */

const EMPTY_FORM = {
  company_id: '',
  record_date: '',
  revenue_amount: '',
  budgets_amount: '',
  closed_budgets_amount: '',
  budgets_count: '',
  closed_budgets_count: '',
  dentists_count: '',
  observations: '',
};

function ClinicalDailyEntryPage() {
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [records, setRecords] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const loadRecords = useCallback(async (companyId) => {
    if (!companyId) {
      setRecords([]);
      return;
    }
    try {
      const { data } = await api.get('/clinical-results/daily', {
        params: { company_id: companyId, limit: 15 },
      });
      setRecords(data.data || []);
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao carregar os lançamentos.'));
    }
  }, []);

  useEffect(() => {
    fetchCompanies()
      .then(setCompanies)
      .catch(() => setError('Falha ao carregar as unidades.'));
  }, []);

  useEffect(() => {
    loadRecords(form.company_id);
  }, [form.company_id, loadRecords]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Preview local — apenas visual; o valor oficial vem do servidor
  const previewConversion = () => {
    const budgets = Number(form.budgets_amount);
    const closed = Number(form.closed_budgets_amount);
    if (!budgets || budgets <= 0) return 0;
    return closed / budgets;
  };

  const resetForm = () => {
    setForm((prev) => ({ ...EMPTY_FORM, company_id: prev.company_id }));
    setEditingId(null);
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const payload = {
        company_id: form.company_id,
        record_date: form.record_date,
        revenue_amount: Number(form.revenue_amount) || 0,
        budgets_amount: Number(form.budgets_amount) || 0,
        closed_budgets_amount: Number(form.closed_budgets_amount) || 0,
        budgets_count: Number(form.budgets_count) || 0,
        closed_budgets_count: Number(form.closed_budgets_count) || 0,
        dentists_count: Number(form.dentists_count) || 0,
        observations: form.observations || null,
      };

      let saved;
      if (editingId) {
        const { data } = await api.put(`/clinical-results/daily/${editingId}`, payload);
        saved = data;
        setRecords((prev) => prev.map((r) => (r.id === saved.id ? saved : r)));
        setSuccess(`Registro de ${fmtDateBR(saved.record_date)} atualizado. Conversão (servidor): ${fmtPct(saved.derived.value_conversion)}.`);
      } else {
        const { data } = await api.post('/clinical-results/daily', payload);
        saved = data;
        setRecords((prev) => [saved, ...prev]);
        setSuccess(`Registro de ${fmtDateBR(saved.record_date)} salvo. Conversão (servidor): ${fmtPct(saved.derived.value_conversion)}.`);
      }
      resetForm();
    } catch (err) {
      // 409 da RN-01 e demais validações: exibir a mensagem da API, não resolver sozinho
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setForm({
      company_id: String(record.company_id),
      record_date: record.record_date,
      revenue_amount: String(record.revenue_amount),
      budgets_amount: String(record.budgets_amount),
      closed_budgets_amount: String(record.closed_budgets_amount),
      budgets_count: String(record.budgets_count),
      closed_budgets_count: String(record.closed_budgets_count),
      dentists_count: String(record.dentists_count),
      observations: record.observations || '',
    });
    setError('');
    setSuccess('');
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Excluir o lançamento de ${fmtDateBR(record.record_date)}?`)) return;
    setError('');
    try {
      await api.delete(`/clinical-results/daily/${record.id}`);
      setRecords((prev) => prev.filter((r) => r.id !== record.id));
      setSuccess('Registro excluído. A trilha permanece na auditoria.');
      if (editingId === record.id) resetForm();
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  return (
    <div className="clinical-page">
      <h1>Lançamento Diário — Resultados da Unidade</h1>
      <p className="clinical-subtitle">Um registro por unidade por dia. Valores derivados são calculados pelo servidor.</p>

      {error && <div className="clinical-error">{error}</div>}
      {success && <div className="clinical-success">{success}</div>}

      <div className="clinical-form">
        <label>
          Unidade
          <select name="company_id" value={form.company_id} onChange={handleChange}>
            <option value="">Selecione...</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>

        <label>
          Data
          <input type="date" name="record_date" value={form.record_date} onChange={handleChange} />
        </label>

        <label>
          Receita total do dia (R$)
          <input type="number" min="0" step="0.01" name="revenue_amount" value={form.revenue_amount} onChange={handleChange} />
        </label>

        <label>
          Valor orçamentos (R$)
          <input type="number" min="0" step="0.01" name="budgets_amount" value={form.budgets_amount} onChange={handleChange} />
        </label>

        <label>
          Valor orç. fechados (R$)
          <input type="number" min="0" step="0.01" name="closed_budgets_amount" value={form.closed_budgets_amount} onChange={handleChange} />
        </label>

        <label>
          Qtd. orçamentos novos
          <input type="number" min="0" step="1" name="budgets_count" value={form.budgets_count} onChange={handleChange} />
        </label>

        <label>
          Qtd. orçamentos fechados
          <input type="number" min="0" step="1" name="closed_budgets_count" value={form.closed_budgets_count} onChange={handleChange} />
        </label>

        <label>
          Profissionais na unidade
          <input type="number" min="0" step="1" name="dentists_count" value={form.dentists_count} onChange={handleChange} />
        </label>

        <label className="form-full">
          Observações
          <textarea name="observations" rows="2" value={form.observations} onChange={handleChange} />
        </label>

        <div className="form-full clinical-preview">
          Prévia da conversão (local): <strong>{fmtPct(previewConversion())}</strong> — o valor oficial é recalculado pelo servidor ao salvar.
        </div>

        <div className="clinical-form-actions">
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={loading || !form.company_id || !form.record_date}
          >
            {loading ? 'Salvando...' : editingId ? 'Atualizar registro' : 'Salvar registro'}
          </button>
          {editingId && (
            <button className="btn-secondary" onClick={resetForm}>Cancelar edição</button>
          )}
        </div>
      </div>

      <h2 className="clinical-section-title">Últimos lançamentos</h2>
      {records.length === 0 ? (
        <p className="clinical-empty">Nenhum lançamento para a unidade selecionada.</p>
      ) : (
        <table className="clinical-table">
          <thead>
            <tr>
              <th>Data</th>
              <th className="num">Receita</th>
              <th className="num">Orçado</th>
              <th className="num">Fechado</th>
              <th className="num">Conversão</th>
              <th className="num">Ticket médio</th>
              <th className="num">Profissionais</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id}>
                <td>{fmtDateBR(r.record_date)}</td>
                <td className="num">{fmtBRL(r.revenue_amount)}</td>
                <td className="num">{fmtBRL(r.budgets_amount)}</td>
                <td className="num">{fmtBRL(r.closed_budgets_amount)}</td>
                <td className="num">{r.derived ? fmtPct(r.derived.value_conversion) : '—'}</td>
                <td className="num">{r.derived ? fmtBRL(r.derived.average_ticket) : '—'}</td>
                <td className="num">{r.dentists_count}</td>
                <td>
                  <button className="btn-edit" onClick={() => handleEdit(r)}>Editar</button>{' '}
                  <button className="btn-danger" onClick={() => handleDelete(r)}>Excluir</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default ClinicalDailyEntryPage;
